import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TutorModelOption, TutorModelsPayload } from "@/lib/ai-tutor/models";

export const TUTOR_MODELS_PATH = "/ai-tutor/models";

export interface TutorModelState {
  /** この人に出すモデル。読めていない間と、1 つしか無いときは切り替えを出さない */
  models: TutorModelOption[];
  /** 選んでいるモデル。読めるまでは null (送る本文に model を付けず、サーバーの既定になる) */
  selected: string | null;
  /** 切り替える。画面はすぐ替え、選んだことはサーバーに残す (失敗しても画面の選択は保つ) */
  select: (id: string) => void;
  /** 送るときに読む。描画をまたいでも最新の選択を返す */
  current: () => string | null;
}

function isPayload(value: unknown): value is TutorModelsPayload {
  const v = value as Partial<TutorModelsPayload> | null;
  return Boolean(v && Array.isArray(v.models) && typeof v.selected === "string");
}

/**
 * AI チューターのモデルの切り替えの状態 (レッスンのチャット、学習パス、クラウドエディターの板で共通)。
 * 一覧は GET /ai-tutor/models を 1 回読む。選んだことは POST で残す。
 *
 * fetcher (useFetcher) を使わないのは次の 2 つのため。
 *   load   画面の action (コードの自動保存など) の読み直しと重なると、読み込みが打ち切られたまま戻らないことがあった
 *          (2026-10-11 に手元で、切り替えが出ない回を見た)
 *   submit 送ったあとに画面の loader が全部読み直される
 * 読めなければ切り替えを出さないだけで、チャットはサーバーの既定 (Haiku) で使える
 */
export function useTutorModel(enabled: boolean): TutorModelState {
  const [data, setData] = useState<TutorModelsPayload | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const selectedRef = useRef<string | null>(null);
  /** 選んだことを残す送信は 1 本ずつ。続けて押したとき、前の送信が後から着いて最後の選択を上書きしないように */
  const saving = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    void fetch(TUTOR_MODELS_PATH, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: unknown) => {
        if (!isPayload(body)) return;
        setData(body);
        if (selectedRef.current === null) {
          selectedRef.current = body.selected;
          setSelected(body.selected);
        }
      })
      .catch(() => {
        /* 読めなければ切り替えを出さない */
      });
    return () => controller.abort();
  }, [enabled]);

  const models = useMemo(() => data?.models ?? [], [data]);
  const select = useCallback(
    (id: string) => {
      if (!models.some((m) => m.id === id) || selectedRef.current === id) return;
      selectedRef.current = id;
      setSelected(id);
      saving.current = saving.current.then(async () => {
        // 待っている間にまた選び直されていたら、この回は送らない (最後の選択だけを送る)
        if (selectedRef.current !== id) return;
        const form = new FormData();
        form.set("model", id);
        await fetch(TUTOR_MODELS_PATH, { method: "POST", body: form, credentials: "same-origin" }).catch(() => {
          /* 残せなくても、この画面の間は選んだモデルで送る */
        });
      });
    },
    [models],
  );
  const current = useCallback(() => selectedRef.current, []);

  return useMemo(() => ({ models, selected, select, current }), [models, selected, select, current]);
}
